/**
 * Dialog shown before starting the API Doctor.
 * Lets the user provide optional guidance and confirms the mode (fix vs improve).
 */

import React from 'react';
import { Dialog, DialogFooter, DialogTrigger, Button, Text, TextArea, Flex } from '@chatsdet/ui';
import { Stethoscope, Sparkle } from '@phosphor-icons/react';
import type { DoctorMode } from './ApiSequenceDoctorView';

type ApiSequenceDoctorLaunchDialogProps = {
  mode: DoctorMode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (guidance: string) => void;
};

export const ApiSequenceDoctorLaunchDialog: React.FC<ApiSequenceDoctorLaunchDialogProps> = ({
  mode,
  isOpen,
  onOpenChange,
  onConfirm,
}) => {
  const [guidance, setGuidance] = React.useState('');
  const isImprove = mode === 'improve';

  const handleConfirm = () => {
    onConfirm(guidance.trim());
    setGuidance('');
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <span style={{ display: 'none' }} />
      <Dialog title={isImprove ? 'Improve API Sequence' : 'Fix API Sequence'}>
        <div className="space-y-4">
          <Text size="sm" className="text-muted-foreground">
            {isImprove
              ? 'The API Doctor will analyze this sequence for improvements — better variable usage, template chaining, missing exports, and other optimizations.'
              : 'The API Doctor will diagnose the failures in this sequence run and propose fixes for the failing steps.'}
          </Text>

          <div className="space-y-1.5">
            <Text size="sm" weight="medium">
              Guidance (optional)
            </Text>
            <TextArea
              value={guidance}
              onChange={(value) => setGuidance(value)}
              placeholder={
                isImprove
                  ? 'e.g., "Use the credential UUID directly instead of variables" or "Add exports for the project ID"'
                  : 'e.g., "The auth header might be missing" or "Try a different endpoint for step 2"'
              }
              className="min-h-[80px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Flex gap="sm">
            <Button variant="ghost" size="sm" onPress={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onPress={handleConfirm}>
              {isImprove ? <Sparkle size={14} /> : <Stethoscope size={14} />}
              {isImprove ? 'Improve Sequence' : 'Run Doctor'}
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
};
